// src/pages/MyGuides.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints, CHROME_STORE_URL } from '../services/api';
import ReactGA from 'react-ga4';
import GuideCard from '../components/GuideCard/GuideCard';
import ShareModal from '../components/ShareModal/ShareModal';
import SkeletonCard from '../components/GuideCard/SkeletonCard';
import '../styles/pages/Page.css';
import { FREE_MODE } from '../config/freeMode';
import { withErrorBoundary } from '../components/ErrorBoundary';
import { getFriendlyError } from '../components/errorUtils';
import Mascot from '../components/Mascot';


const PAGE_SIZE = 9;

function trackChromeStoreClick(source) {
  ReactGA.event('chrome_store_click', {
    click_source: source,
  });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const MyGuides = () => {
  const [guides, setGuides] = useState([]);
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(true);   // initial fetch
  const [loadingMore, setLoadingMore] = useState(false);  // infinite-scroll fetch
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [sharingGuide, setSharingGuide] = useState(null);

  const { token } = useAuth();
  const navigate = useNavigate();
  const skipRef = useRef(0);   // tracks current offset
  const sentinelRef = useRef(null); // element watched by IntersectionObserver
  const observerRef = useRef(null);
  const isFetchingRef = useRef(false);

  // -------------------------------------------------------------------------
  // Fetch a single page of guides
  // -------------------------------------------------------------------------
  const fetchGuides = useCallback(async (skip) => {
    if (!token) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    skip === 0 ? setLoading(true) : setLoadingMore(true);
    setError('');

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const guidesUrl = `${endpoints.guides.base}?skip=${skip}&limit=${PAGE_SIZE}`;

      // Fire guides + plan fetch in parallel on the initial load
      const requests = [fetch(guidesUrl, { headers })];
      if (skip === 0) requests.push(fetch(endpoints.auth.me, { headers }));

      const [guidesRes, planRes] = await Promise.all(requests);

      if (!guidesRes.ok) {
        const err = await guidesRes.json();
        throw new Error(err.detail || 'Failed to fetch guides');
      }

      const { items, has_more } = await guidesRes.json();
      setHasMore(has_more);
      setGuides(prev => skip === 0 ? items : [...prev, ...items]);
      skipRef.current = skip + items.length;

      if (planRes?.ok) {
        const planData = await planRes.json();
        if (planData) setUserPlan(planData);
      }
    } catch (err) {
      setError(getFriendlyError(err.message, 'My Guides'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [token]);

  // -------------------------------------------------------------------------
  // Initial load
  // -------------------------------------------------------------------------
  useEffect(() => {
    skipRef.current = 0;
    setGuides([]);
    setHasMore(true);
    fetchGuides(0);
  }, [fetchGuides]);

  // -------------------------------------------------------------------------
  // IntersectionObserver — fires when sentinel scrolls into view
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchGuides(skipRef.current);
        }
      },
      { rootMargin: '800px' }  // start loading 800px before the sentinel
    );

    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);

    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loading, fetchGuides]);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------
  const handleDeleteGuide = async (guideId) => {
    setError('');
    try {
      const response = await fetch(endpoints.guides.detail(guideId), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete guide. Please try again.');
      }
      setGuides(prev => prev.filter(g => g.id !== guideId));
    } catch (err) {
      setError(getFriendlyError(err.message, 'My Guides'));
    }
  };

  const buildPdfName = (guide) => {
    const fallback = `guide_${guide?.id ?? 'download'}`;
    const raw = (guide?.name || fallback).trim();
    const safe = raw.replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '') || fallback;
    return `${safe}.pdf`;
  };

  const handleDownloadGuide = async (guide) => {
    setError('');
    if (!guide?.id) { setError(getFriendlyError('Missing guide id for download.', 'My Guides')); return; }
    try {
      const response = await fetch(endpoints.guides.exportPdf(guide.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const j = await response.json().catch(() => ({ detail: 'Failed to download PDF' }));
        throw new Error(j.detail || 'Failed to download PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildPdfName(guide);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getFriendlyError(err.message, 'My Guides'));
    }
  };

  const handleDownloadGuidePrivacy = async (guide) => {
    setError('');
    if (!guide?.id) { setError(getFriendlyError('Missing guide id for download.', 'My Guides')); return; }
    try {
      const response = await fetch(`${endpoints.guides.exportPdf(guide.id)}?privacy=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const j = await response.json().catch(() => ({ detail: 'Failed to download PDF' }));
        throw new Error(j.detail || 'Failed to download PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fallback = `guide_${guide?.id ?? 'download'}`;
      const raw = (guide?.name || fallback).trim();
      const safe = raw.replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '') || fallback;
      a.download = `${safe}_private.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getFriendlyError(err.message, 'My Guides'));
    }
  };

  const handleGuideUpdate = (updatedGuide) => {
    setGuides(prev => prev.map(g => g.id === updatedGuide.id ? { ...g, ...updatedGuide } : g));
  };

  // -------------------------------------------------------------------------
  // Derived plan state
  // -------------------------------------------------------------------------
  const plan = userPlan?.plan;
  const isFreePlan = plan === 'free';
  // /me reports a trial as "individual" and sets trial_ends_at only for unpaid accounts
  const isTrial = plan === 'individual' && !!userPlan?.trial_ends_at;
  const isIndividualPlan = plan === 'individual' && !isTrial;
  const isTeamsPlan = plan === 'teams';
  const trialDaysLeft = isTrial
    ? Math.max(0, Math.ceil((new Date(userPlan.trial_ends_at) - Date.now()) / 86400000))
    : 0;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="page-container">
      <div className="container">
        <header className="m-page-head">
          <div>
            <h1>My Guides</h1>
            <p>Everything you&apos;ve recorded. Open a guide to play it, share it or export a PDF.</p>
          </div>
        </header>

        {/* ── Plan banners ──────────────────────────────────────────── */}
        {/* FREE_MODE: restore all plan banners below when FREE_MODE=false */}
        {!FREE_MODE && !loading && userPlan && isTrial && (
          <div className="plan-banner">
            <div className="plan-banner__info">
              <span className="plan-banner__badge">Free trial</span>
              <span className="plan-banner__text">
                {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left · Unlimited guides · AI chat
              </span>
            </div>
            <a href="/#pricing" className="plan-banner__upgrade">Choose a plan</a>
          </div>
        )}

        {!FREE_MODE && !loading && userPlan && isFreePlan && (
          <div className="plan-banner plan-banner--limit">
            <div className="plan-banner__info">
              <span className="plan-banner__badge">Trial ended</span>
              <span className="plan-banner__text">
                Your guides still play and can be shared. Upgrade to record new guides.
              </span>
            </div>
            <a href="/#pricing" className="plan-banner__upgrade">Upgrade</a>
          </div>
        )}

        {!FREE_MODE && !loading && userPlan && isIndividualPlan && (
          <div className="plan-banner">
            <div className="plan-banner__info">
              <span className="plan-banner__badge">Individual Plan</span>
              <span className="plan-banner__text">Unlimited guides · AI chat · No watermark</span>
            </div>
          </div>
        )}

        {!FREE_MODE && !loading && userPlan && isTeamsPlan && (
          <div className="plan-banner">
            <div className="plan-banner__info">
              <span className="plan-banner__badge">Teams Plan</span>
              <span className="plan-banner__text">Collaborative workspace · Unlimited guides · AI chat</span>
            </div>
            <a href="/workspace" className="plan-banner__upgrade">View workspace</a>
          </div>
        )}

        {!FREE_MODE && !loading && userPlan && isFreePlan && (
          <div className="plan-notice">
            <span>AI chat is available on paid plans.</span>
            <a href="/#pricing" className="plan-notice__link">Upgrade to unlock</a>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────────── */}
        {error && <p className="page-error">{error}</p>}

        {/* ── Empty State ───────────────────────────────────────────── */}
        {!loading && !error && guides.length === 0 && (
          <div className="empty-state">
            <Mascot size={64} className="empty-state__icon" />
            <div>
              <p className="m-eyebrow">Michii says</p>
              <h2 className="empty-state__title">Nothing recorded yet.</h2>
              <p className="empty-state__desc">
                You haven&apos;t created any guides yet. Click the Michii icon in your browser
                toolbar and do any task the way you normally would. Your first guide shows up here.
              </p>
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="empty-state__cta"
                onClick={() => trackChromeStoreClick('my_guides_empty_state')}
              >
                Get the Chrome extension
              </a>
              <span className="empty-state__note m-hand">already installed? click its icon ↗</span>
            </div>
          </div>
        )}

        {/* ── Guide grid ────────────────────────────────────────────── */}
        {(guides.length > 0 || loading) && (
          <div className="guide-grid">
            {/* Initial skeleton — 9 cards while first page loads */}
            {loading &&
              Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <SkeletonCard key={`sk-${i}`} cardIndex={i % PAGE_SIZE} />
              ))
            }

            {guides.map((guide, i) => (
              <GuideCard
                key={guide.id}
                guide={guide}
                cardIndex={i % PAGE_SIZE}
                showDelete={true}
                onDelete={handleDeleteGuide}
                showDownload={true}
                onDownload={handleDownloadGuide}
                onDownloadPrivacy={handleDownloadGuidePrivacy}
                userPlan={plan}
                isOwner={guide.is_owner}
                onShare={() => setSharingGuide(guide)}
                onEdit={() => navigate(`/guides/${guide.id}?edit=1`)}
              />
            ))}

            {/* Skeleton rows while loading the next page */}
            {loadingMore &&
              Array.from({ length: 3 }).map((_, i) => (
                <SkeletonCard key={`sk-more-${i}`} cardIndex={i % PAGE_SIZE} />
              ))
            }
          </div>
        )}

        {/* ── Sentinel — observed by IntersectionObserver ───────────── */}
        {!loading && !error && (
          <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
        )}

        {/* ── End-of-list message ───────────────────────────────────── */}
        {!loading && !hasMore && guides.length > 0 && (
          <p className="guides-end-label">All guides loaded</p>
        )}
      </div>

      {sharingGuide && (
        <ShareModal
          guide={sharingGuide}
          token={token}
          onClose={() => setSharingGuide(null)}
          onUpdate={handleGuideUpdate}
        />
      )}

    </div>
  );
};

export default withErrorBoundary(MyGuides, 'MyGuides');